import { useEffect, useState } from 'react';
import screens from './Screens.module.css';
import styles from './History.module.css';
import { Badge, Button, Spinner } from '../../components/ui';
import { getHistory } from '../../api/platform.js';
import { forecastSummary, parseForecast } from '../../api/forecastText.js';
import { formatDate, formatDateTime, useI18n } from '../../i18n/index.jsx';

/**
 * Saved forecasts.
 *
 * What was predicted and at what probability is read out of the stored forecast
 * text. Outcome badges appear on rows the backend has graded (`result` /
 * `actual_winner`); rows without those fields simply show no outcome.
 *
 * Free accounts get {locked: true} with an empty list rather than a 403.
 */
export function History() {
  const { t, lang } = useI18n();
  const [state, setState] = useState({ status: 'loading' });
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    document.title = `${t('nav_history')} — Don Quack`;
  }, [t]);

  useEffect(() => {
    let cancelled = false;
    getHistory()
      .then((res) => {
        if (cancelled) return;
        setState({
          status: res.locked ? 'locked' : 'ready',
          items: res.items.map((item) => ({
            ...item,
            parsed: parseForecast(item.forecastText, splitSides(item.matchName)),
            summary: forecastSummary(item.forecastText),
          })),
        });
      })
      .catch((err) => !cancelled && setState({ status: 'error', message: err.message }));
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section>
      <h2 className={screens.title}>{t('history_title')}</h2>
      <p className={screens.sub}>{t('history_subtitle')}</p>

      {state.status === 'loading' && (
        <div className={screens.loadingBlock}>
          <Spinner />
        </div>
      )}

      {state.status === 'error' && <div className={screens.errorBox}>{state.message}</div>}

      {state.status === 'locked' && (
        <div className={styles.gate}>
          <span className={styles.lock} aria-hidden="true">
            🔒
          </span>
          <h3>{t('history_locked_title')}</h3>
          <p className="muted">{t('history_locked_body')}</p>
          <Button to="/app/pricing">{t('gate_cta')}</Button>
        </div>
      )}

      {state.status === 'ready' &&
        (state.items.length === 0 ? (
          <div className={screens.empty}>
            <h3>{t('history_no_forecasts')}</h3>
            <p>{t('history_no_forecasts_body')}</p>
            <Button to="/app/forecast">{t('wizard_title')}</Button>
          </div>
        ) : (
          <div className={styles.list}>
            {state.items.map((item) => {
              const probs = item.parsed.probabilities;
              const badge = outcomeBadge(item, t);
              const isOpen = openId === item.id;

              return (
                <div key={item.id} className={styles.item}>
                  <button
                    type="button"
                    className={styles.row}
                    aria-expanded={isOpen}
                    onClick={() => setOpenId(isOpen ? null : item.id)}
                  >
                    <span className={styles.date}>
                      {item.matchDate
                        ? formatDate(item.matchDate, lang, { day: 'numeric', month: 'short' })
                        : '—'}
                    </span>

                    <span className={styles.match}>
                      {item.matchName}
                      <span>
                        {probs
                          ? `${t('forecast_predicted_winner')} · ${probs.predictedWinner}`
                          : item.league}
                      </span>
                    </span>

                    {badge && (
                      <Badge tone={badge.tone} dot={badge.tone !== 'low'}>
                        {badge.label}
                      </Badge>
                    )}

                    {probs && (
                      <span className={`${styles.prob} mono-num`}>
                        {Math.max(probs.homeProbability, probs.awayProbability)}%
                      </span>
                    )}
                    <span className={styles.chevron} aria-hidden="true">
                      {isOpen ? '−' : '+'}
                    </span>
                  </button>

                  {isOpen && (
                    <div className={styles.detail}>
                      {item.actualWinner && (
                        <p className={styles.actual}>
                          {t('actual_winner')} · <b>{item.actualWinner}</b>
                        </p>
                      )}
                      {item.gradedAt && (
                        <p className={styles.generated}>
                          {t('graded_on', {
                            date: formatDateTime(item.gradedAt, lang),
                          })}
                        </p>
                      )}
                      {item.generatedAt && (
                        <p className={styles.generated}>
                          {t('generated_on', {
                            date: formatDateTime(item.generatedAt, lang),
                          })}
                        </p>
                      )}
                      <pre className={styles.forecastText}>{item.forecastText}</pre>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
    </section>
  );
}

/** Badge tone and label for a graded row. Returns null when not gradeable. */
function outcomeBadge(item, t) {
  if (!item.isGradeable) return null;

  // Present but null: the match hasn't been graded yet.
  if (item.result === null) return { tone: 'low', label: t('outcome_pending') };
  if (item.result === 'correct') return { tone: 'win', label: t('outcome_correct') };
  if (item.result === 'incorrect') return { tone: 'loss', label: t('outcome_missed') };
  if (item.result === 'void') return { tone: 'low', label: t('outcome_void') };
  return null;
}

/** "A vs B" -> {home, away}, so the stored text can be parsed per side. */
function splitSides(name = '') {
  const parts = String(name).split(/\s+(?:vs\.?|v\.?|—|-)\s+/i);
  return parts.length >= 2
    ? { home: parts[0].trim(), away: parts.slice(1).join(' ').trim() }
    : { home: String(name).trim(), away: '' };
}
